import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isNotNullish } from '@bcef/ia-shared';
import { WA_WINDOW } from '@ng-web-apis/common';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxMaskConfig } from 'ngx-mask';
import { forkJoin, map, Observable, of, switchMap, withLatestFrom } from 'rxjs';

import { DeceasedMoreInformationFormValues } from '@features/succession/interfaces';
import {
  ConfirmationInformation,
  SummaryInformation,
} from '@features/succession/interfaces/summary-information.interface';
import { CustomerInformation, ListBoxItem, ListBoxItems } from '@features/succession/models';

import {
  CustomerInformationConfirmation,
  CustomerInformationReload,
  CustomerInformationResetWorkflow,
  CustomerInformationSaveAndLoad,
  selectConfirmationInformation,
  selectCustomerInformation,
  selectCustomerInformationIsLoading,
  selectDeceasedMoreInformationFormValues,
  selectListBoxItems,
  selectLoadPage,
  selectStepNumber,
  selectSummaryInformation,
  selectToken,
} from './store';

@Injectable({ providedIn: 'root' })
export class SuccessionFacade {
  protected destroyRef = inject(DestroyRef);

  protected actions$ = inject(Actions);

  private readonly store = inject(Store);

  private readonly translate = inject(TranslateService);

  private readonly window: Window = inject(WA_WINDOW);

  public specialCharactersNotAllowed: NgxMaskConfig['patterns'] = {
    A: { pattern: new RegExp('[^:\'";()&<>*^\x00|]') },
  };

  public numbersAndSpecialCharactersNotAllowed: NgxMaskConfig['patterns'] = {
    A: { pattern: new RegExp('[^0-9:\'";()&<>*^\x00|]') },
  };

  public today = new Date();

  public listBoxItems: ListBoxItems = new ListBoxItems();

  public token = '';

  public stepNumber$: Observable<number> = this.store.select(selectStepNumber);

  public customerInformationIsLoading$: Observable<boolean> = this.store.select(
    selectCustomerInformationIsLoading,
  );

  public customerInformation$: Observable<CustomerInformation> = this.store
    .select(selectCustomerInformation)
    .pipe(isNotNullish());

  public deceasedMoreInformationFormValues$: Observable<DeceasedMoreInformationFormValues> =
    this.store.select(selectDeceasedMoreInformationFormValues);

  public summaryInformation$: Observable<SummaryInformation> =
    this.store.select(selectSummaryInformation);

  public confirmationInformation$: Observable<ConfirmationInformation> = this.store.select(
    selectConfirmationInformation,
  );

  public isNextPageLoading = false;

  public isPreviousPageLoading = false;

  public isNextPageLoading$: Observable<boolean> = this.actions$.pipe(
    ofType(CustomerInformationSaveAndLoad),
    withLatestFrom(this.store.select(selectLoadPage)),
    map(
      ([{ customerInformation }, currentPage]) =>
        Number(customerInformation.loadPage) > currentPage,
    ),
  );

  public isPreviousPageLoading$: Observable<boolean> = this.actions$.pipe(
    ofType(CustomerInformationSaveAndLoad),
    withLatestFrom(this.store.select(selectLoadPage)),
    map(
      ([{ customerInformation }, currentPage]) =>
        Number(customerInformation.loadPage) < currentPage,
    ),
  );

  public steps$: Observable<string[]> = this.stepNumber$.pipe(
    switchMap((stepNumber: number) => {
      if (stepNumber > 6) {
        return of([]);
      }
      return of([1, 2, 3, 4, 5, 6]);
    }),
    switchMap((steps: number[]) =>
      forkJoin(steps.map((step) => this.translate.get(`header.stepper.step${step}.title`))),
    ),
  );

  public hasSteps$: Observable<boolean> = this.stepNumber$.pipe(
    map((stepNumber) => stepNumber <= 6),
  );

  constructor() {
    this.actions$
      .pipe(
        ofType(CustomerInformationSaveAndLoad),
        withLatestFrom(this.store.select(selectLoadPage)),
      )
      .subscribe(([{ customerInformation }, currentPage]) => {
        this.isPreviousPageLoading = Number(customerInformation.loadPage) < currentPage;
        this.isNextPageLoading = Number(customerInformation.loadPage) > currentPage;
      });

    this.store
      .select(selectListBoxItems)
      .pipe(isNotNullish(), takeUntilDestroyed(this.destroyRef))
      .subscribe((listBoxItems: ListBoxItems) => {
        this.listBoxItems = listBoxItems;
      });

    this.store
      .select(selectToken)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((token?: string) => {
        this.token = token || '';
      });
  }

  public saveAndLoad(customerInformation: CustomerInformation): void {
    this.store.dispatch(CustomerInformationSaveAndLoad({ customerInformation }));
  }

  public reloadPage(): void {
    this.store.dispatch(CustomerInformationReload());
  }

  public goBack(): void {
    this.store.dispatch(CustomerInformationResetWorkflow());
  }

  public confirmation(): void {
    this.store.dispatch(CustomerInformationConfirmation());
  }

  public listBoxItemValue(item: ListBoxItem | ListBoxItem[] | null): string | ListBoxItem[] | null {
    if (!Array.isArray(item) && item !== null) {
      return item.value;
    }
    return item;
  }

  public trackNext(): void {
    this.window._satellite?.track('clickCtaDeclarationDeces', {
      libelleButton: 'Suivant',
      events: [17],
    });
  }

  public trackPrevious(): void {
    this.window._satellite?.track('clickCtaDeclarationDeces', {
      libelleButton: 'Precedent',
      events: [17],
    });
  }
}
